import React, { useEffect, useRef, useState } from "react";
import { Bookmark } from "lucide-react";
import { BACKEND_URL } from "../config";
import type { TreatmentPreset } from "../utils/treatmentPresets";

interface PresetRow { id: number; text: string; treatment_template: TreatmentPreset | null }

export default function TreatmentPresetBookmarks({ onLoad }: { onLoad: (complaint: string, preset: TreatmentPreset) => void }) {
  const [rows, setRows] = useState<PresetRow[]>([]);
  const [query, setQuery] = useState("");
  const [busy, setBusy] = useState<number | null>(null);
  const [message, setMessage] = useState("");
  const active = useRef(true);
  useEffect(() => {
    active.current = true;
    const controller = new AbortController();
    fetch(`${BACKEND_URL}/api/clinical-text-suggestions?category=chief_complaint`, { credentials: "include", signal: controller.signal })
      .then(async (res) => { if (!res.ok) throw new Error("Unable to load treatment bookmarks"); return res.json(); })
      .then((data: PresetRow[]) => { if (active.current) setRows(data.filter((row) => row.treatment_template)); })
      .catch((err) => { if (active.current && err.name !== "AbortError") setMessage(err.message); });
    return () => { active.current = false; controller.abort(); };
  }, []);

  const load = async (row: PresetRow) => {
    if (busy !== null) return;
    setBusy(row.id); setMessage("");
    try {
      const res = await fetch(`${BACKEND_URL}/api/clinical-text-suggestions/${row.id}/treatment-preset`, { credentials: "include" });
      if (!res.ok) throw new Error("Unable to load this preset. Refresh and try again.");
      const result = await res.json();
      if (!active.current) return;
      onLoad(result.text, result.treatment_template);
      setMessage(`Loaded ${result.text}. Review the added medicines and treatment details before saving.`);
      if (result.treatment_template.warnings?.length) setMessage(`Loaded available items. Not loaded: ${result.treatment_template.warnings.join("; ")}`);
    } catch (err: any) { if (active.current) setMessage(err.message); }
    finally { if (active.current) setBusy(null); }
  };

  return <section className="mb-5 rounded border border-border-light bg-background-light p-3">
    <div className="mb-2 font-bold">Treatment bookmarks</div>
    <p className="mb-2 text-sm text-subtext-light">Load a saved chief-complaint preset. Existing medicines and filled clinical fields are kept.</p>
    <input aria-label="Search treatment bookmarks" placeholder="Find a complaint, e.g. headache" value={query} onChange={(e) => setQuery(e.target.value)} className="mb-3 w-full rounded border p-2" />
    <div className="flex max-h-40 flex-wrap gap-2 overflow-auto">
      {rows.filter((row) => row.text.toLowerCase().includes(query.trim().toLowerCase())).map((row) => <button key={row.id} type="button" disabled={busy !== null}
        onClick={() => void load(row)} title={row.treatment_template?.medicines.map((item) => item.medicine_name).join(", ")}
        className="inline-flex items-center gap-2 rounded border border-primary px-3 py-2 text-sm font-semibold text-primary disabled:opacity-50">
        <Bookmark size={16} />{busy === row.id ? "Loading..." : row.text} ({row.treatment_template?.medicines.length || 0})
      </button>)}
    </div>
    {!rows.length && <p className="text-sm text-subtext-light">Add treatment presets in Configuration → Chief Complaint.</p>}
    {message && <p role="status" className="mt-3 text-sm font-semibold">{message}</p>}
  </section>;
}
